/**
 * Cart Drawer Discount Functionality
 */
class CartDrawerDiscount {
    constructor() {
      this.storageKey = 'floukyCartDiscountCodes';
      this.init();
    }
  
    init() {
      this.discountInput = document.querySelector(".cart-drawer__discount-input");
      this.discountButton = document.querySelector(
        ".cart-drawer__discount-button"
      );
      this.discountError = document.querySelector(".cart-drawer__discount-error");
      
      // Get all remove discount buttons
      this.discountRemoveButtons = document.querySelectorAll(
        '.cart-drawer__remove-discount'
      );
  
      if (this.discountInput && this.discountButton) {
        this.attachEventListeners();
      }
  
      // Attach listeners to all remove buttons
      this.attachRemoveListeners();
      this.syncDiscountsWithCart(); // Sync on init
    }
  
    getStoredDiscounts() {
      try {
        const storedCodes = localStorage.getItem(this.storageKey);
        return storedCodes ? JSON.parse(storedCodes) : [];
      } catch (error) {
        console.error('Error reading discounts from localStorage:', error);
        return [];
      }
    }
  
    setStoredDiscounts(codes) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(codes));
      } catch (error) {
        console.error('Error saving discounts to localStorage:', error);
      }
    }
  
    async syncDiscountsWithCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        // Filter for applicable codes only
        const applicableCodes = (cart.discount_codes || [])
          .filter(d => d.applicable)
          .map(d => d.code);
        this.setStoredDiscounts(applicableCodes);
      } catch (error) {
        console.error('Error syncing discounts with cart:', error);
      }
    }
  
    attachEventListeners() {
      // Remove any existing listeners first
      this.removeEventListeners();
  
      // Store bound functions for removal later
      this.boundHandleKeypress = this.handleKeypress.bind(this);
      this.boundHandlePaste = this.handlePaste.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundApplyDiscount = this.applyDiscount.bind(this);
  
      // Prevent comma input
      this.discountInput.addEventListener("keypress", this.boundHandleKeypress);
  
      // Handle paste to remove commas
      this.discountInput.addEventListener("paste", this.boundHandlePaste);
  
      // Handle Enter key
      this.discountInput.addEventListener("keydown", this.boundHandleKeydown);
  
      // Handle button click
      this.discountButton.addEventListener("click", this.boundApplyDiscount);
    }
  
    attachRemoveListeners() {
      // Remove any existing listeners
      if (this.boundRemoveListeners) {
        this.boundRemoveListeners.forEach(({ button, handler }) => {
          button.removeEventListener("click", handler);
        });
      }
  
      this.boundRemoveListeners = [];
  
      // Attach new listeners to all remove buttons
      this.discountRemoveButtons.forEach(button => {
        const discountCode = button.getAttribute('data-discount-code');
        const handler = () => this.removeDiscount(discountCode);
        button.addEventListener("click", handler);
        this.boundRemoveListeners.push({ button, handler });
      });
    }
  
    removeEventListeners() {
      if (this.discountInput) {
        this.discountInput.removeEventListener(
          "keypress",
          this.boundHandleKeypress
        );
        this.discountInput.removeEventListener("paste", this.boundHandlePaste);
        this.discountInput.removeEventListener(
          "keydown",
          this.boundHandleKeydown
        );
      }
  
      if (this.discountButton) {
        this.discountButton.removeEventListener("click", this.boundApplyDiscount);
      }
  
      // Remove all discount remove button listeners
      if (this.boundRemoveListeners) {
        this.boundRemoveListeners.forEach(({ button, handler }) => {
          button.removeEventListener("click", handler);
        });
      }
    }
  
    handleKeypress(e) {
      if (e.key === ",") {
        e.preventDefault();
      }
    }
  
    handlePaste(e) {
      e.preventDefault();
      const pastedText = (e.clipboardData || window.clipboardData).getData(
        "text"
      );
      const cleanedText = pastedText.replace(/,/g, "");
      this.discountInput.value = cleanedText;
    }
  
    handleKeydown(e) {
      if (e.key === "Enter") {
        e.preventDefault();
        this.applyDiscount();
      }
    }
  
    async applyDiscount() {
      const newDiscountCode = this.discountInput.value.trim();
      if (!newDiscountCode) return;
  
      this.discountButton.disabled = true;
      this.discountInput.disabled = true;
  
      try {
        const storedCodes = this.getStoredDiscounts();
        if (storedCodes.map(c => c.toLowerCase()).includes(newDiscountCode.toLowerCase())) {
          this.showError();
          this.discountInput.value = '';
        } else {
          const codesToApply = [...storedCodes, newDiscountCode];
          
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ discount: codesToApply.join(',') }),
          });
          const cartData = await response.json();
  
          const applicableCodesFromServer = (cartData.discount_codes || [])
            .filter(d => d.applicable)
            .map(d => d.code);
  
          this.setStoredDiscounts(applicableCodesFromServer);
  
          const newCodeWasApplied = applicableCodesFromServer
            .map(c => c.toLowerCase())
            .includes(newDiscountCode.toLowerCase());
  
          if (newCodeWasApplied) {
            this.discountInput.value = '';
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          } else {
            this.showError();
            
            setTimeout(async () => {
              const cleanupResponse = await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ discount: applicableCodesFromServer.join(',') || '' }),
              });
              
              const finalCartData = await cleanupResponse.json();
              const finalApplicableCodes = (finalCartData.discount_codes || [])
                .filter(d => d.applicable)
                .map(d => d.code);
              
              this.setStoredDiscounts(finalApplicableCodes);
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }, 3000);
          }
        }
      } catch (error) {
        console.error('Discount application error:', error);
        this.showError();
      } finally {
        this.discountButton.disabled = false;
        this.discountInput.disabled = false;
        this.discountInput.focus();
      }
    }
  
    async removeDiscount(discountCodeToRemove) {
      if (!discountCodeToRemove) return;
      
      try {
        let storedCodes = this.getStoredDiscounts();
        const codesToApply = storedCodes.filter(c => c.toLowerCase() !== discountCodeToRemove.toLowerCase());
  
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ discount: codesToApply.join(',') || '' }), // Send empty string if no discounts left
        });
  
        if (response.ok) {
          const cartData = await response.json();
          this.setStoredDiscounts(cartData.discount_codes.map(d => d.code));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (error) {
        console.error('Discount removal error:', error);
      }
    }
  
    showError() {
      this.discountError.hidden = false;
  
      // Hide error after 5 seconds
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }
  
      this.errorTimeout = setTimeout(() => {
        this.discountError.hidden = true;
      }, 5000);
    }
  
    destroy() {
      this.removeEventListeners();
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }
    }
  }
  
  // Global instance
  let cartDrawerDiscountInstance = null;
  
  // Function to initialize or reinitialize
  function initializeCartDrawerDiscount() {
    // Destroy existing instance and create new one
    if (cartDrawerDiscountInstance) {
      cartDrawerDiscountInstance.destroy();
    }
  
    cartDrawerDiscountInstance = new CartDrawerDiscount();
  }
  
  // Initialize when DOM is ready
  document.addEventListener("DOMContentLoaded", () => {
    initializeCartDrawerDiscount();
    if (cartDrawerDiscountInstance) {
      cartDrawerDiscountInstance.syncDiscountsWithCart();
    }
  });
  
  // Reinitialize when cart is refreshed, and also on initial load
  document.addEventListener("cart:refresh", () => {
    setTimeout(() => {
      initializeCartDrawerDiscount();
      if (cartDrawerDiscountInstance) {
        cartDrawerDiscountInstance.syncDiscountsWithCart();
      }
    }, 500);
  });
  
  document.addEventListener("cart:change", () => {
    setTimeout(() => {
      initializeCartDrawerDiscount();
      if (cartDrawerDiscountInstance) {
        cartDrawerDiscountInstance.syncDiscountsWithCart();
      }
    }, 500);
  });
  
  // ADDITIONAL EVENTS FOR THEME CUSTOMIZER
  // Handle Shopify section events for theme editor
  document.addEventListener("shopify:section:load", (event) => {
    if (event.detail.sectionId.includes("cart-drawer")) {
      setTimeout(() => {
        initializeCartDrawerDiscount();
        if (cartDrawerDiscountInstance) {
          cartDrawerDiscountInstance.syncDiscountsWithCart();
        }
      }, 500);
    }
  });
  
  document.addEventListener("shopify:section:unload", (event) => {
    if (
      event.detail.sectionId.includes("cart-drawer") &&
      cartDrawerDiscountInstance
    ) {
      cartDrawerDiscountInstance.destroy();
    }
  });
  